import React, { useState, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { SpotlightTransition } from '@atlaskit/onboarding';
import { token } from '@atlaskit/tokens';

import {
	ErrorType,
	Severity,
	Product,
	Team,
	createProductSpecificAnalyticsIntegrationPayload,
} from '@atlassian/growth-commons-error-analytics';

import {
	confluenceLocalStorageInstance as localStorage,
	PERSISTED_KEYS_ON_SERVER,
} from '@confluence/storage-manager';
import { useChoreographerAssets } from '@confluence/choreographer-services';
import { Attribution, TransparentErrorBoundary } from '@confluence/error-boundary';
import { AccessStatus, useSessionData } from '@confluence/session-data';
import { isLocaleEnglish } from '@confluence/onboarding-helpers/entry-points/isLocaleEnglish';

import { useEnrolledInRtPaidFeatureEducationABExperiment } from '../hooks/useEnrolledInRtPaidFeatureEducationExperiment';
import { useRtPaidFeatureEducationState } from '../hooks/useRtPaidFeatureEducationStore';

const i18n = defineMessages({
	heading: {
		id: 'experiment-reverse-trial-paid-feature-education.spotlight.heading',
		defaultMessage: 'See the features you have access to during your trial — all in one guide.',
		description:
			'The heading of the spotlight that informs the user that they have access to features during their trial',
	},
	buttonDismiss: {
		id: 'experiment-reverse-trial-paid-feature-education.spotlight.button.dismiss',
		defaultMessage: 'Dismiss',
		description: 'When the user clicks the dismiss button, the spotlight is dismissed',
	},
	buttonGoToGuide: {
		id: 'experiment-reverse-trial-paid-feature-education.spotlight.button.goToGuide',
		defaultMessage: 'Go to guide',
		description: 'When the user clicks the go to guide button, they are redirected to the guide',
	},
});

export const RtPaidFeatureEducationSpotlight = () => {
	const intl = useIntl();
	const { locale, accessStatus } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const AFTER_DAY_7_OF_TRIAL = 23;

	const skipIfNotEnglishLocale = !isLocaleEnglish(locale);
	const isLicensedUser =
		accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
		accessStatus === AccessStatus.LICENSED_USE_ACCESS;

	const { Spotlight } = useChoreographerAssets();

	const { templatePageIdWebUi } = useRtPaidFeatureEducationState();

	const { isTreatment, loading, error, daysLeftInTrial, isInTrialOrPreDunning } =
		useEnrolledInRtPaidFeatureEducationABExperiment({
			skip: skipIfNotEnglishLocale || !isLicensedUser,
		});

	const setUserHasSeenSpotlight = useCallback(() => {
		localStorage.setItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_RT_PAID_FEATURE_EDUCATION_SPOTLIGHT_SEEN,
			true,
		);
	}, []);

	const userHasSeenSpotlight = useCallback(() => {
		const userHasSeenSpotlight = localStorage.getItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_RT_PAID_FEATURE_EDUCATION_SPOTLIGHT_SEEN,
		);
		return userHasSeenSpotlight === 'true';
	}, []);

	const [isSpotlightOpen, setIsSpotlightOpen] = useState(true);

	const hasNotSeenSpotlight = !userHasSeenSpotlight();

	const isValidTrialDayOrPredunning =
		isInTrialOrPreDunning &&
		daysLeftInTrial !== undefined &&
		daysLeftInTrial <= AFTER_DAY_7_OF_TRIAL;

	const isReadyToRender =
		!loading &&
		!error &&
		isTreatment &&
		isSpotlightOpen &&
		hasNotSeenSpotlight &&
		isValidTrialDayOrPredunning;

	const closeSpotlight = useCallback(() => {
		setIsSpotlightOpen(false);
		setUserHasSeenSpotlight();
	}, [setUserHasSeenSpotlight]);

	const onDismiss = useCallback(() => {
		closeSpotlight();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'rtPaidFeatureEducationSpotlightDismiss',
				source: 'RtPaidFeatureEducationSpotlight',
			},
		}).fire();
	}, [createAnalyticsEvent, closeSpotlight]);

	const handleGoToGuide = useCallback(() => {
		closeSpotlight();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'rtPaidFeatureEducationSpotlightGoToGuide',
				source: 'RtPaidFeatureEducationSpotlight',
			},
		}).fire();
	}, [createAnalyticsEvent, closeSpotlight]);

	if (!isReadyToRender) {
		return null;
	}

	return (
		<SpotlightTransition>
			<Spotlight
				actions={[
					{
						text: intl.formatMessage(i18n.buttonGoToGuide),
						onClick: handleGoToGuide,
						href: `/wiki${templatePageIdWebUi}`,
					},
					{
						onClick: onDismiss,
						text: intl.formatMessage(i18n.buttonDismiss),
						appearance: 'subtle',
					},
				]}
				target="rt-paid-feature-education-spotlight"
				key="rt-paid-feature-education-spotlight"
				data-testid="rt-paid-feature-education-spotlight"
				targetRadius={3}
				dialogPlacement="right middle"
				dialogWidth={272}
				targetBgColor={token('elevation.surface')}
			>
				{intl.formatMessage(i18n.heading)}
			</Spotlight>
		</SpotlightTransition>
	);
};

export const RtPaidFeatureEducationSpotlightWithErrorBoundary = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return (
		<TransparentErrorBoundary
			attribution={Attribution.GROWTH_LUNA}
			onError={(error, info) => {
				const eventPayload = createProductSpecificAnalyticsIntegrationPayload({
					product: Product.CONFLUENCE,
					team: Team.GROWTH_LUNA,
					experimentMeta: {
						experimentKey: 'rt_paid_feature_education_ab',
					},
					errorType: ErrorType.ERROR_BOUNDARY,
					errorMessage: error.message,
					severity: Severity.MEDIUM,
					source: 'RtPaidFeatureEducationSpotlightWithErrorBoundary',
					additionalAttributes: {
						...info,
					},
				});

				createAnalyticsEvent(eventPayload).fire();
			}}
		>
			<RtPaidFeatureEducationSpotlight />
		</TransparentErrorBoundary>
	);
};
